/**
 * Utility functions for localStorage operations with consent checking and user-specific namespacing
 */

// Helper function to get a cookie value by name
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

/**
 * Checks if the user has consented to using localStorage
 * @returns true if the user has consented, false otherwise
 */
export const hasStorageConsent = (): boolean => {
  try {
    // Try to get consent from localStorage
    const localStorageConsent = localStorage.getItem('localStorage_consent') === 'true';
    if (localStorageConsent) {
      return true;
    }
    
    // Check for consent in cookies as a fallback
    const cookieConsent = getCookie('localStorage_consent') === 'true';
    if (cookieConsent) {
      // If we found it in cookies but not in localStorage, try to sync them
      try {
        localStorage.setItem('localStorage_consent', 'true');
      } catch (e) {
        // Ignore errors when trying to sync
      }
      return true;
    }
    
    return false;
  } catch (error) {
    // Check just cookies if localStorage access throws an error
    return getCookie('localStorage_consent') === 'true';
  }
};

/**
 * Sets an item in localStorage with consent checking and JSON serialization
 * @param key The key to set
 * @param value The value to set (will be JSON stringified)
 * @returns true if the operation was successful, false otherwise
 */
export const setLocalStorageItem = <T>(key: string, value: T): boolean => {
  // Special case: essential system values should always be allowed
  if (key === 'localStorage_consent' || key === 'authToken' || key === 'userEmail') {
    try {
      // Handle string values directly, serialize other types
      if (typeof value === 'string') {
        localStorage.setItem(key, value);
      } else {
        localStorage.setItem(key, JSON.stringify(value));
      }
      return true;
    } catch (error) {
      console.error(`Error setting ${key} in localStorage:`, error);
      return false;
    }
  }

  // For all other keys, check consent first
  if (hasStorageConsent()) {
    try {
      // Handle string values directly, serialize other types
      if (typeof value === 'string') {
        localStorage.setItem(key, value);
      } else {
        localStorage.setItem(key, JSON.stringify(value));
      }
      return true;
    } catch (error) {
      return false;
    }
  }
  
  return false; // Consent not given
};

/**
 * Gets an item from localStorage with consent checking and JSON parsing
 * @param key The key to get
 * @param defaultValue The default value to return if key doesn't exist or consent is not given
 * @returns The value from localStorage (parsed if JSON), or defaultValue
 */
export const getLocalStorageItem = <T>(key: string, defaultValue: T): T => {
  // Special case: these critical values should always be retrieved regardless of consent
  if (key === 'localStorage_consent' || key === 'authToken' || key === 'userEmail') {
    try {
      const value = localStorage.getItem(key);
      if (value === null) return defaultValue;
      
      // Try to parse as JSON, fallback to string value
      try {
        return JSON.parse(value) as T;
      } catch {
        // If it's not valid JSON but we expect a string
        if (typeof defaultValue === 'string') {
          return value as unknown as T;
        }
        return defaultValue;
      }
    } catch (error) {
      console.error(`Error retrieving ${key} from localStorage:`, error);
      return defaultValue;
    }
  }

  // For all other keys, check consent first
  if (hasStorageConsent()) {
    try {
      const value = localStorage.getItem(key);
      if (value === null) return defaultValue;
      
      // Try to parse as JSON, fallback to string value
      try {
        return JSON.parse(value) as T;
      } catch {
        // If it's not valid JSON but we expect a string
        if (typeof defaultValue === 'string') {
          return value as unknown as T;
        }
        return defaultValue;
      }
    } catch (error) {
      return defaultValue;
    }
  }
  
  return defaultValue; // Consent not given
};

/**
 * Removes an item from localStorage with consent checking
 * @param key The key to remove
 * @returns true if the operation was successful, false otherwise
 */
export const removeLocalStorageItem = (key: string): boolean => {
  // Special case: essential items should always be allowed to be removed without consent check
  if (key === 'localStorage_consent' || key === 'authToken' || key === 'userEmail') {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing ${key} from localStorage:`, error);
      return false;
    }
  }

  // For all other keys, check consent first
  if (hasStorageConsent()) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  }
  
  return false; // Consent not given
};

/**
 * Get a user-specific setting from localStorage
 * Uses user email as a namespace to keep settings separate
 * @param email The user's email for namespacing
 * @param key The setting key
 * @param fallback The default value if not found
 * @returns The setting value or fallback
 */
export function getUserSetting<T>(email: string | null, key: string, fallback: T): T {
  if (!email) return fallback;
  
  const userKey = `user_${email.replace(/[^a-zA-Z0-9]/g, '_')}_${key}`;
  return getLocalStorageItem(userKey, fallback);
}

/**
 * Set a user-specific setting in localStorage
 * Uses user email as a namespace to keep settings separate
 * @param email The user's email for namespacing
 * @param key The setting key
 * @param value The value to store
 */
export function setUserSetting<T>(email: string | null, key: string, value: T): void {
  if (!email) return;
  
  const userKey = `user_${email.replace(/[^a-zA-Z0-9]/g, '_')}_${key}`;
  setLocalStorageItem(userKey, value);
}

/**
 * Remove a user-specific setting from localStorage
 * @param email The user's email for namespacing
 * @param key The setting key to remove
 */
export function removeUserSetting(email: string | null, key: string): void {
  if (!email) return;
  
  const userKey = `user_${email.replace(/[^a-zA-Z0-9]/g, '_')}_${key}`;
  removeLocalStorageItem(userKey);
}

/**
 * Clear all user-specific settings from localStorage for a given email
 * @param email The user's email whose settings should be cleared
 */
export function clearUserSettings(email: string | null): void {
  if (!email) return;
  
  const prefix = `user_${email.replace(/[^a-zA-Z0-9]/g, '_')}_`;
  
  // Iterate through all localStorage keys
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      removeLocalStorageItem(key);
    }
  }
} 